<template lang="pug">
.bulk-sends-card-table
  card-table(:total-items='data.length' breakpoint-size='lg')
    template(v-for='(item, index) in data' v-slot:[`card-${index}`]='{}')
      .position-relative
        router-link(
          :aria-label='$t("bulkSendsCardTable.details")'
          :to='getDetailsRoute(item)'
          style='position: absolute; top: 0; right: 0; font-size: 1.25em'
          v-tooltip:top='$t("bulkSendsCardTable.details")'
        )
          icon.icon--blue(name='magnifying-glass')
        .mb-1.text-overflow-ellipse(style='width: 90%') {{ item.name }}
        .mb-1 {{ $t('bulkSendsCardTable.createdAt') }}: {{ item.created_at | dateTime }}
        .mb-0
          icon.me-2(
            :class='getStatusIcon(item).class'
            :name='getStatusIcon(item).name'
          )
          span.text.me-2 {{ getStatusText(item) }}
        .bulk-sends-card-table__signatures.mt-2(v-if='showSignatures(item)')
          .progress
            .progress-bar(
              :aria-valuenow='getProgress(item)'
              :class='getProgressClass(item)'
              :style='{ width: `${getProgress(item)}%` }'
              aria-valuemax='100'
              aria-valuemin='0'
              role='progressbar'
            )
          small.text-nowrap {{ getSignedCount(item) }}
        .bulk-sends-card-table__signatures.mt-2(v-else-if='isProcessing(item)')
          .progress
            .progress-bar.progress-bar--processing.progress-bar-striped.progress-bar-animated(
              aria-valuemax='100'
              aria-valuemin='0'
              role='progressbar'
            )
          small.text-nowrap {{ $t('bulkSendsCardTable.generatingEnvelopes') }}
    template(v-slot:table-head)
      th(style='width: 35%') {{ $t('bulkSendsCardTable.name') }}
      th(style='width: 25%') {{ $t('bulkSendsCardTable.signatures') }}
      th(style='width: 20%')
        .d-flex
          span.text {{ $t('bulkSendsCardTable.createdAt') }}
          button(
            @click='$emit("add-sort", "created_at", "DESC")'
            style='margin-top: -2px'
            v-show='sort === "created_at"'
          )
            icon.icon--blue(name='angle-down')
          button(
            @click='$emit("add-sort", "created_at", "ASC")'
            style='margin-top: -2px'
            v-show='sort === "-created_at"'
          )
            icon.icon--blue(name='angle-up')
      th(style='width: 19%') {{ $t('bulkSendsCardTable.status') }}
      th(style='width: 1%')
    template(v-for='(item, index) in data' v-slot:[`table-row-${index}`]='{}')
      td
        .bulk-sends-card-table__name.text-overflow-ellipse(:title='item.name') {{ item.name }}
      td
        .bulk-sends-card-table__signatures(v-if='showSignatures(item)')
          .progress
            .progress-bar(
              :aria-valuenow='getProgress(item)'
              :class='getProgressClass(item)'
              :style='{ width: `${getProgress(item)}%` }'
              aria-valuemax='100'
              aria-valuemin='0'
              role='progressbar'
            )
          small.text-nowrap {{ getSignedCount(item) }}
        .bulk-sends-card-table__signatures(v-else-if='isProcessing(item)')
          .progress
            .progress-bar.progress-bar--processing.progress-bar-striped.progress-bar-animated(
              aria-valuemax='100'
              aria-valuemin='0'
              role='progressbar'
            )
          small.text-nowrap {{ $t('bulkSendsCardTable.generatingEnvelopes') }}
        template(v-else)
      td {{ item.created_at | dateTime }}
      td
        icon.me-2(
          :class='getStatusIcon(item).class'
          :name='getStatusIcon(item).name'
        )
        span.text {{ getStatusText(item) }}
      td
        .d-flex
          button.ms-2(
            :title='$t("bulkSendsCardTable.edit")'
            @click='openConfigureBulkSend(item)'
            style='color: inherit'
            v-if='isDraftBulkSend(item)'
          )
            icon(name='pen-to-square') 
      td
        router-link(
          :aria-label='$t("bulkSendsCardTable.details")'
          :to='getDetailsRoute(item)'
          v-tooltip:top='$t("bulkSendsCardTable.details")'
        )
          icon.icon--blue(name='magnifying-glass')
</template>
<script>
import CardTable from '@/ui/organisms/CardTable.vue';
import Icon from '@/ui/atoms/Icon.vue';
import BulkSendStatus from '@/enums/BulkSendStatus';
import BulkSendService from '@/modules/business/services/BulkSendService';

const PROGRESS_LEVELS = [
  { min: 100, name: 'complete' },
  { min: 34, name: 'medium' },
  { min: 0, name: 'low' },
];

export default {
  name: 'BulkSendsCardTable',
  components: {
    CardTable,
    Icon,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    sort: {
      type: String,
      required: true,
    },
  },
  methods: {
    getDetailsRoute(item) {
      return {
        name: 'business.documents.bulk-send-details',
        params: { uuid: item.uuid },
      };
    },
    getStatusIcon(item) {
      return BulkSendStatus.getIcon(BulkSendService.getDisplayStatus(item));
    },
    getStatusText(item) {
      return BulkSendStatus.getText(
        BulkSendService.getDisplayStatus(item),
        this.$i18n
      );
    },
    getEnvelopesCount(item) {
      return {
        completed: item.envelopes?.completed || 0,
        total: item.envelopes?.total || 0,
      };
    },
    showSignatures(item) {
      return (
        Boolean(item.envelopes?.total) ||
        BulkSendService.getDisplayStatus(item) === BulkSendStatus.DISPATCHED
      );
    },
    isProcessing(item) {
      return item.status === BulkSendStatus.PROCESSING;
    },
    getProgress(item) {
      const { completed, total } = this.getEnvelopesCount(item);

      if (!total) {
        return 0;
      }

      return Math.round((completed / total) * 100);
    },
    getProgressClass(item) {
      const progress = this.getProgress(item);
      const level = PROGRESS_LEVELS.find(({ min }) => progress >= min);
      return `progress-bar--${level.name}`;
    },
    getSignedCount(item) {
      return this.$t(
        'bulkSendsCardTable.signedCount',
        this.getEnvelopesCount(item)
      );
    },
    isDraftBulkSend(item) {
      return BulkSendService.checkBulkSendIsDraft(item);
    },
    async openConfigureBulkSend(item) {
      this.$loading(true);
      await this.$router.push({
        path: `/business/bulk-send/${item.uuid}/template`,
        query: { editDraftBulkSend: true },
      });
      this.$loading(false);
    },
  },
};
</script>
<style lang="stylus">
.bulk-sends-card-table__name
  max-width: 180px
@media (min-width: 1200px)
  .bulk-sends-card-table__name
    max-width: 300px
.bulk-sends-card-table__signatures
  max-width: 200px
  .progress
    height: 8px
    margin-bottom: 4px
    background-color: #E6E6E6
    border-radius: 4px
  .progress-bar
    border-radius: 4px
    transition: width 0.4s ease
    &--low
      background-color: #F5A623
    &--medium
      background-color: #39D5C5
    &--complete
      background-color: #0B5C4E
    &--processing
      width: 100%
      background-color: #C9D2D2
</style>
